import { useState, useEffect } from 'react';
import { api, formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  LifeBuoy, Plus, ArrowLeft, Send, Clock, CheckCircle2, AlertCircle,
  Loader2, MessageSquare, Tag,
} from 'lucide-react';

const STATUS_CONFIG = {
  open: { label: 'Abierto', color: 'bg-blue-500/10 text-blue-400', icon: AlertCircle },
  in_progress: { label: 'En Proceso', color: 'bg-amber-500/10 text-amber-400', icon: Clock },
  resolved: { label: 'Resuelto', color: 'bg-emerald-500/10 text-emerald-400', icon: CheckCircle2 },
  closed: { label: 'Cerrado', color: 'bg-zinc-500/10 text-zinc-500', icon: CheckCircle2 },
};

const CATEGORY_LABELS = {
  general: 'General', billing: 'Facturacion', technical: 'Tecnico',
  complaint: 'Queja', suggestion: 'Sugerencia',
};

export default function SupportPage() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newTicket, setNewTicket] = useState({ subject: '', category: 'general', message: '' });

  useEffect(() => { loadTickets(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTickets = async () => {
    setLoading(true);
    try {
      const data = await api.getMyTickets();
      setTickets(data.tickets || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const openTicket = async (ticketId) => {
    setDetailLoading(true);
    try {
      const data = await api.getTicket(ticketId);
      setSelectedTicket(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDetailLoading(false); }
  };

  const handleCreate = async () => {
    if (!newTicket.subject.trim() || !newTicket.message.trim()) {
      toast.error('Asunto y mensaje son requeridos');
      return;
    }
    setCreating(true);
    try {
      await api.createTicket(newTicket);
      toast.success('Ticket creado exitosamente');
      setShowCreate(false);
      setNewTicket({ subject: '', category: 'general', message: '' });
      loadTickets();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCreating(false); }
  };

  const handleReply = async () => {
    if (!replyText.trim() || !selectedTicket) return;
    setSending(true);
    try {
      await api.replyTicket(selectedTicket.id, { message: replyText });
      setReplyText('');
      openTicket(selectedTicket.id);
      toast.success('Respuesta enviada');
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  const handleClose = async () => {
    if (!selectedTicket) return;
    try {
      await api.closeTicket(selectedTicket.id);
      toast.success('Ticket cerrado');
      setSelectedTicket(null);
      loadTickets();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Detail view
  if (selectedTicket) {
    const sc = STATUS_CONFIG[selectedTicket.status] || STATUS_CONFIG.open;
    return (
      <div className="space-y-6" data-testid="support-ticket-detail">
        <div className="flex items-center gap-3">
          <button onClick={() => setSelectedTicket(null)}
            className="p-2 rounded-lg hover:bg-white/5 text-zinc-400" data-testid="back-to-tickets">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-zinc-100 truncate">{selectedTicket.subject}</h2>
            <div className="flex items-center gap-2 mt-1">
              <span className={`text-[10px] px-2 py-0.5 rounded-full ${sc.color}`}>{sc.label}</span>
              <span className="text-[10px] text-zinc-600">{CATEGORY_LABELS[selectedTicket.category]}</span>
              <span className="text-[10px] text-zinc-700">
                {new Date(selectedTicket.created_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })}
              </span>
            </div>
          </div>
          {selectedTicket.status !== 'closed' && (
            <Button variant="outline" size="sm" onClick={handleClose}
              className="border-white/10 text-zinc-400 text-xs h-8" data-testid="close-ticket-btn">
              Cerrar ticket
            </Button>
          )}
        </div>

        {/* Messages */}
        <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2" data-testid="ticket-messages">
          {detailLoading ? (
            <div className="text-center py-8"><Loader2 className="w-5 h-5 text-zinc-600 animate-spin mx-auto" /></div>
          ) : (selectedTicket.messages || []).map((msg) => {
            const isAdmin = msg.sender_role === 'admin';
            return (
              <div key={msg.id} className={`flex ${isAdmin ? 'justify-start' : 'justify-end'}`}>
                <div className={`max-w-[75%] rounded-xl p-3 ${
                  isAdmin ? 'bg-red-500/5 border border-red-500/10' : 'bg-white/[0.04] border border-white/[0.06]'
                }`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[10px] font-medium ${isAdmin ? 'text-red-400' : 'text-zinc-400'}`}>
                      {isAdmin ? 'Soporte MorphMX' : msg.sender_name || 'Tu'}
                    </span>
                    <span className="text-[10px] text-zinc-700">
                      {new Date(msg.created_at).toLocaleString('es-MX', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  <p className="text-sm text-zinc-200 whitespace-pre-wrap">{msg.content}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Reply */}
        {selectedTicket.status !== 'closed' && (
          <div className="flex gap-2" data-testid="ticket-reply-section">
            <Input
              value={replyText} onChange={e => setReplyText(e.target.value)}
              placeholder="Escribe tu respuesta..."
              className="flex-1 bg-white/[0.03] border-white/5 text-zinc-100 h-10 text-sm"
              onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleReply()}
              data-testid="ticket-reply-input"
            />
            <Button onClick={handleReply} disabled={sending || !replyText.trim()}
              className="bg-purple-600 hover:bg-purple-500 text-white h-10 px-4" data-testid="send-reply-btn">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        )}
      </div>
    );
  }

  // Tickets list
  return (
    <div className="space-y-6" data-testid="support-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-zinc-100">Soporte</h2>
          <p className="text-xs text-zinc-600">Crea y gestiona tus tickets de soporte</p>
        </div>
        <Button onClick={() => setShowCreate(true)}
          className="bg-purple-600 hover:bg-purple-500 text-white h-9 text-xs" data-testid="new-ticket-btn">
          <Plus className="w-3.5 h-3.5 mr-1.5" /> Nuevo Ticket
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-16"><Loader2 className="w-5 h-5 text-zinc-600 animate-spin mx-auto" /></div>
      ) : tickets.length === 0 ? (
        <div className="text-center py-16">
          <LifeBuoy className="w-10 h-10 text-zinc-800 mx-auto mb-3" />
          <p className="text-sm text-zinc-500">No tienes tickets de soporte</p>
          <p className="text-xs text-zinc-700 mt-1">Crea uno si necesitas ayuda</p>
        </div>
      ) : (
        <div className="space-y-2">
          {tickets.map(t => {
            const sc = STATUS_CONFIG[t.status] || STATUS_CONFIG.open;
            const StatusIcon = sc.icon;
            const lastMsg = t.messages?.[0];
            return (
              <button key={t.id} onClick={() => openTicket(t.id)}
                className="w-full flex items-center gap-3 p-4 rounded-xl border border-white/[0.04] bg-[#0A0A0D] hover:bg-white/[0.02] transition-colors text-left"
                data-testid={`ticket-${t.id}`}>
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${sc.color}`}>
                  <StatusIcon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm text-zinc-200 font-medium truncate">{t.subject}</p>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0 ${sc.color}`}>{sc.label}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[10px] text-zinc-600">{CATEGORY_LABELS[t.category]}</span>
                    <span className="text-[10px] text-zinc-700">
                      {new Date(t.updated_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })}
                    </span>
                    {lastMsg && (
                      <span className="text-[10px] text-zinc-600 truncate max-w-[200px]">{lastMsg.content}</span>
                    )}
                  </div>
                </div>
                <MessageSquare className="w-4 h-4 text-zinc-700 flex-shrink-0" />
              </button>
            );
          })}
        </div>
      )}

      {/* Create Ticket Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-[#121215] border-white/10 max-w-md" data-testid="create-ticket-dialog">
          <DialogHeader>
            <DialogTitle className="text-zinc-100">Nuevo Ticket de Soporte</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs text-zinc-500 mb-1.5 block">Asunto</label>
              <Input value={newTicket.subject} onChange={e => setNewTicket(p => ({ ...p, subject: e.target.value }))}
                placeholder="Describe brevemente tu problema..."
                className="bg-white/5 border-white/10 text-zinc-100 h-9 text-sm" data-testid="ticket-subject-input" />
            </div>
            <div>
              <label className="text-xs text-zinc-500 mb-1.5 block">Categoria</label>
              <Select value={newTicket.category} onValueChange={v => setNewTicket(p => ({ ...p, category: v }))}>
                <SelectTrigger className="bg-white/5 border-white/10 text-zinc-100 h-9 text-sm" data-testid="ticket-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#121215] border-white/10">
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="billing">Facturacion</SelectItem>
                  <SelectItem value="technical">Tecnico</SelectItem>
                  <SelectItem value="complaint">Queja</SelectItem>
                  <SelectItem value="suggestion">Sugerencia</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-zinc-500 mb-1.5 block">Mensaje</label>
              <textarea value={newTicket.message} onChange={e => setNewTicket(p => ({ ...p, message: e.target.value }))}
                placeholder="Explica tu problema o pregunta en detalle..."
                rows={4}
                className="w-full rounded-md bg-white/5 border border-white/10 text-zinc-100 text-sm p-3 resize-none focus:outline-none focus:ring-1 focus:ring-purple-500/50"
                data-testid="ticket-message-input" />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowCreate(false)}
                className="border-white/10 text-zinc-400 h-9 text-xs">Cancelar</Button>
              <Button onClick={handleCreate} disabled={creating}
                className="bg-purple-600 hover:bg-purple-500 text-white h-9 text-xs" data-testid="submit-ticket-btn">
                {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
                Enviar Ticket
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
